import { getAuthToken } from './auth';

type EventAttendanceRecord = {
  transactionID?: string;
  ticketContractVersion?: number;
  ticketInventoryStatus?: string;
  state?: string;
  paymentMethod?: string;
  tickets?: Array<{
    confirmationID?: string;
    pendingRefundRequestID?: string | null;
    ticketInventoryReleaseReasons?: unknown[];
  }>;
};

export type CancelableAttendance = NonNullable<EventAttendanceRecord['tickets']>[number];

const hasValidAttendanceLedger = (
  tickets: EventAttendanceRecord['tickets'],
): tickets is NonNullable<EventAttendanceRecord['tickets']> =>
  Array.isArray(tickets) &&
  tickets.length > 0 &&
  tickets.every(
    (attendee) =>
      attendee != null &&
      typeof attendee === 'object' &&
      typeof attendee.confirmationID === 'string' &&
      attendee.confirmationID.trim().length > 0 &&
      Array.isArray(attendee.ticketInventoryReleaseReasons) &&
      attendee.ticketInventoryReleaseReasons.every(
        (reason) => typeof reason === 'string' && reason.trim().length > 0,
      ),
  );

export const getActiveEventAttendance = (
  payment?: EventAttendanceRecord | null,
): CancelableAttendance[] => {
  if (!hasValidAttendanceLedger(payment?.tickets)) return [];
  return payment.tickets.filter(
    (attendee) => attendee.confirmationID && !attendee.ticketInventoryReleaseReasons?.length,
  );
};

export const getCancelableAttendanceConfirmationIDs = (
  payment?: EventAttendanceRecord | null,
): string[] =>
  getActiveEventAttendance(payment).flatMap((attendee) =>
    attendee.confirmationID && !attendee.pendingRefundRequestID ? [attendee.confirmationID] : [],
  );

export const canCancelEventAttendance = (payment?: EventAttendanceRecord | null): boolean => {
  const activeAttendees = getActiveEventAttendance(payment);
  return Boolean(
    payment?.transactionID &&
      payment.ticketContractVersion === 2 &&
      ['SUCCEEDED', 'PARTIAL_REFUND'].includes(payment.state || '') &&
      ['ACTIVE', 'PARTIALLY_RELEASED', 'OVER_CAPACITY', 'NEEDS_REMEDIATION'].includes(
        payment.ticketInventoryStatus || '',
      ) &&
      activeAttendees.some((attendee) => !attendee.pendingRefundRequestID),
  );
};

export const canCancelFreeEventAttendance = canCancelEventAttendance;

export async function cancelEventAttendance(
  transactionID: string,
  confirmationIDs: string[],
  operationID: string,
  noRefundAcknowledged: boolean,
) {
  const token = await getAuthToken();
  const response = await fetch(
    `${import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL}/checkout/cancel-event-attendance`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        transactionID,
        operationID,
        confirmationIDs,
        noRefundAcknowledged,
      }),
    },
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.message || 'Unable to cancel attendance');
  }
  return result;
}
